<template>
    <section id="legal-data">
        <div class="list-group-item collapse-element d-flex justify-content-between align-items-center">
            <b>{{$t('co.conclusion.variable-title', { currentStep: currentStep, steps: numberOfSteps })}}</b>
        </div>
        <b-collapse
            class="list-group-item checkout-payment-box "
            tag="div"
            v-model="isVisible"
        >
            <div class="row">
                <div class="col-12 payment-data mt-3">
                    <h2 class="display-3 mb-2">{{$t('co.conclusion.survey')}}</h2>
                    <p v-if="checkPersonalData">
                        <b>{{ $t('co.conclusion.personalData')}}</b><br>
                        <span>{{ getCustomerType }}</span><br>
                        <span v-if="getCompany">
                            {{ personalData.company.value }}
                            <span v-if="personalData.company_legal_form.value">
                                {{$t(`co.personal_data.company.type.${personalData.company_legal_form.value}`)}}
                            </span>
                        </span><br>
                        <span>{{ getFullName }}</span><br>
                        <span v-if="ageVerificationNeeded">{{ $t('co.personal_data.birthday') }}: {{ getBirthday }}<br></span>
                        <span>{{ $t('co.personal_data.telephone') }}: {{ getTelephone }}</span>
                    </p>
                    <p v-else>
                        <b>{{ $t('co.conclusion.personalData')}}</b><br>
                        <span>{{ $t('co.conclusion.personalData.reminder')}}</span>
                    </p>

                    <b>{{ $t('co.conclusion.addressData')}}</b><br>

                    <div class="row mb-3">
                        <div class="col-12 col-md-6">
                            <b>
                                {{ $t('co.address_data.delivery_address') }}
                                <span v-if="deliveryEqualsInvoice">
                                    & {{ $t('cdb.subscriptions.invoice_address') }}
                                </span>
                            </b><br>
                            <span v-html="getDeliveryAddress"></span>
                        </div>
                        <div class="col-12 col-md-6">
                            <span v-if="!deliveryEqualsInvoice" class="mt-3">
                                <b>{{ $t('cdb.subscriptions.invoice_address') }}</b><br>
                                <span v-html="getInvoiceAddress"></span>
                            </span>
                        </div>
                    </div>

                    <p v-if="paymentHandling">
                        <b>{{ $t('co.conclusion.paymentData') }}</b>
                        <component
                            class="mb-3"
                            v-if="selectedPaymentMethod !== null"
                            v-bind:is="getPaymentComponent"
                            :selectedPaymentMethod="selectedPaymentMethod"
                        ></component>
                    </p>
                    <p>
                        <b>{{ $t('cdb.subscriptions.handover') }}</b><br>
                        {{ getDeliveryMethod }}
                    </p>

                    <p v-if="!this.sameAddress">
                        <b>{{$t('pds.delivery_cost.pickup.location')}}</b><br>
                        {{ this.pickupDisplayLocation }}
                    </p>
                    <p v-if="this.hasPickupLocation && !this.sameAddress">
                        <b>{{$t('pds.delivery_cost.return.location')}}</b><br>
                        {{ this.returnDisplayLocation }}
                    </p>
                    <p  v-if="this.hasPickupLocation && this.sameAddress">
                        <b>{{$t('pds.delivery_cost.pickup.return.location')}}</b><br>
                        {{ this.pickupDisplayLocation }}
                    </p>

                    <p><b>{{$t('co.conclusion.survey.costs')}}</b></p>
                    <ul class="list-unstyled">
                        <li v-if="entry && (entry.additional_driver || entry.dp_reduction)" class="d-flex justify-content-between">
                            <span>{{ $t('co.product.monthly_vehicle_rate') }}</span>
                            <span class="pl-2">{{ getPriceWithoutAdditionalCosts }} / {{ $t('pds.buybox.permonth') }}</span>
                        </li>
                        <li v-if="entry && entry.dp_reduction" class="d-flex justify-content-between">
                            <span>{{ entry.dp_reduction_article.label }}</span>
                            <span class="pl-2">{{ $n(entry.dp_reduction_article.price, 'currency') }} / {{ $t('pds.buybox.permonth') }}</span>
                        </li>
                        <li v-if="entry && entry.additional_driver" class="d-flex justify-content-between">
                            <span>{{ entry.additional_driver_article.label }} &ndash; {{ entry.additional_driver_details.name }}</span>
                            <span class="pl-2">{{ $n(entry.additional_driver_article.price, 'currency') }} / {{ $t('pds.buybox.permonth') }}</span>
                        </li>
                        <li v-if="entry && (entry.additional_driver || entry.dp_reduction)">
                            <hr />
                        </li>
                        <li class="d-flex justify-content-between">
                            <span>
                                <font-awesome-icon :icon="['fas', 'check']" />
                                <span v-if="withoutTax">{{$t('co.conclusion.survey.costs.net_all_inclusive_price')}}</span>
                                <span v-else>{{$t('co.conclusion.survey.costs.all_inclusive_price')}}</span>
                                <span :title="$t('co.conclusion.survey.coupon.tooltip')">
                                    <i><font-awesome-icon icon="fa-info-circle" /></i>
                                </span>
                            </span>
                            <span v-if="coupon.state">
                                <del>{{ getPrice }}</del> {{ getPriceWithCoupon }}
                            </span>
                            <span v-else>{{ getPrice }}</span>
                        </li>
                        <li class="d-flex justify-content-between" v-if="coupon.state">
                            <span>
                                <font-awesome-icon :icon="['fas', 'check']" />
                                {{$t('co.conclusion.survey.costs.coupon_success')}}
                            </span>
                            <span v-if="this.couponType !== null && this.couponType === 'percent'">
                                -{{ getCouponValue }} %
                            </span>
                            <span v-if="this.couponType !== null && this.couponType !== 'percent' && !withoutTax">
                                -{{ getCouponValue }} €
                            </span>
                            <span v-if="this.couponType !== null && this.couponType !== 'percent' && withoutTax">
                                -{{ getCouponValueWithoutTax }}
                            </span>
                        </li>
                        <li class="d-flex justify-content-between" v-if="!willBePickedUp">
                            <span>
                                <font-awesome-icon :icon="['fas', 'check']" />
                                <span v-if="withoutTax">{{$t('co.conclusion.survey.costs.delivery.net')}}</span>
                                <span v-else>{{$t('co.conclusion.survey.costs.delivery')}}</span>
                            </span>
                            <span>{{ getDeliveryCosts }}</span>
                        </li>
                    </ul>
                </div>
                <div class="col-12 py-4" v-if="entry && !entry.coupon">
                    <b-form-group
                        :label="$t('co.conclusion.survey.coupon')"
                        label-for="coupon"
                        :valid-feedback="$t('co.conclusion.survey.valid')"
                        :invalid-feedback="$t('co.conclusion.survey.invalid')"
                        :state="coupon.state"
                    >
                        <b-form-input
                            id="coupon-payment"
                            placeholder="Code"
                            type="text"
                            v-model="coupon.value"
                            @change="validateCoupon"
                            :state="coupon.state"
                        ></b-form-input>
                    </b-form-group>

                </div>
                <div class="col-12 pb-4" v-if="this.$root.whitelabel.setting('type') == 'marketplace'">
                    <b-form-checkbox v-if="isMarketplaceOwner === true" class="pb-1" v-model="tocCheck"><p v-html="this.$t('co.conclusion.confirm.gtc', {agb: this.$getUrl('content-page', { path: 'agb' })})"></p></b-form-checkbox>
                    <b-form-checkbox v-if="isMarketplaceOwner === false" class="pb-1" v-model="tocCheck"><p v-html="this.$t('co.conclusion.confirm.gtc.mp', {agb: this.$getUrl('content-page', { path: 'agb' }), customer_agb: this.$root.whitelabel.setting('customer_agb_file_'+this.product.subsidiary.uuid), mp_participant: this.product.subsidiary.name})"></p></b-form-checkbox>
                    <b-form-checkbox v-if="!this.$root.whitelabel.isUs()" v-model="privacyCheck"><p v-html="$t('co.conclusion.confirm.privacy', {privacy: $getUrl('content-page', { path: 'datenschutz' })})"></p></b-form-checkbox>
                </div>
                <div class="col-12 pb-4" v-else>
                    <b-form-checkbox class="pb-1" v-model="tocCheck"><p v-html="$t('co.conclusion.confirm.gtc', {agb: $getUrl('content-page', { path: 'agb' })})"></p></b-form-checkbox>
                    <b-form-checkbox v-if="!this.$root.whitelabel.isUs()" v-model="privacyCheck"><p v-html="$t('co.conclusion.confirm.privacy', {privacy: $getUrl('content-page', { path: 'datenschutz' })})"></p></b-form-checkbox>
                </div>
            </div>
            <div class="row d-flex justify-content-end mt-4">
                <div class="col-12 d-flex justify-content-end">
                    <button type="button" class="btn btn-primary btn-mobile-full-width" @click="submit" v-if="isValid && !this.isSubmitting">{{$t('co.conclusion.button.order_with_costs')}}</button>
                    <button type="button" class="btn btn-primary btn-mobile-full-width disabled" v-if="!isValid && !this.isSubmitting">{{$t('co.conclusion.button.order_with_costs')}}</button>
                    <button type="button" class="btn btn-primary btn-mobile-full-width disabled" v-if="this.isSubmitting">{{$t('co.button.loading')}}</button>
                </div>
            </div>
        </b-collapse>
    </section>
</template>

<script>
import { BCollapse, BFormCheckbox, BLink, BFormInput, BFormGroup,BButton } from "bootstrap-vue"
import StripeCardInfo from '../atoms/StripeCardInfo';
import StripeSepaInfo from '../atoms/StripeSepaInfo';
import StripeNoInfo from '../atoms/StripeNoInfo';
import { EventBus } from '../../event-bus'

export default {
    name: 'CheckoutLegalData',
    components: {
        BButton,
        BCollapse, BFormCheckbox, BLink, BFormInput, BFormGroup,
        StripeCardInfo,
        StripeSepaInfo,
        StripeNoInfo,
    },
    props: {
        user: null,
        product: null,
        currentStep: {
            type: Number,
        },
        numberOfSteps: {
            type: Number,
        },
    },
    data() {
        return {
            isVisible: false,
            tocCheck: false,
            privacyCheck: false,
            isSubmitting: false,
            deliveryCheck: false,
            coupon: {
                value: '',
                percent: 0,
                state: null
            },
            entry: null,
            selectedPaymentMethod: {},
            personalData: null,
            personalDataError: 0,
            couponType: null,
            couponValue: null,
        }
    },
    methods: {
        validateCoupon() {
            this.isSubmitting = true
            const { subid } = this.$root.whitelabel.state
            const price = this.$root.whitelabel.setting('show_price_without_taxes') ? this.entry.price_plan.price_without_tax : this.entry.price_plan.price;
            
            if (this.coupon.value) {
                this.$axios.put('api/v1/checkout/validate-coupon', {
                    coupon: this.coupon.value,
                    price: price,
                    vehicle_id: this.entry.vehicle.id,
                }).then(res => {
                    if (typeof res.data.data !== 'undefined') {
                        this.couponType = res.data.data.type;
                        this.couponValue = res.data.data.value;
                        this.coupon.state = true
                    } else {
                        this.coupon.state = false
                    }
                    this.isSubmitting = false
                }).catch(err => {
                    this.isSubmitting = false
                    this.error = true
                })
            } else {
                this.isSubmitting = false
                this.coupon.state = false
            }
        },
        submit() {
            this.isSubmitting = true
            this.$axios.put('api/v1/checkout/update_subscription', {
                subscription_uuid : this.entry.uuid,
                agb_accepted : true,
                privacy_accepted : true,
            }).then(response => {
                if (response.data == 'vehicle_not_available') {
                    alert(this.$t('checkout.redirect_error'))
                    this.$router.push('/')
                }

                if (response.data) {
                    this.checkoutSubscription()
                } else {
                    this.error = true
                }
            }).catch(error => {
                this.isSubmitting = false
                this.error = true
            });
        },
        checkoutSubscription() {
            this.$axios.post('api/v1/checkout/checkout_subscription', {
                subscription_uuid : this.entry.uuid,
                coupon: this.coupon.value
            }).then(response => {
                if (response.data) {
                    this.$goTo('checkout-thanks', { identifier: this.entry.identifier })
                } else {
                    this.error = true
                }
            }).catch(error => {
                this.isSubmitting = false
                this.error = true
            });
        },
        init() {
            if (this.getIdentifier() === null) {
                this.isLoading = false
                this.isBroken = true
            } else {
                this.isLoading = false
                this.$axios.post('api/v1/dashboard/show_subscriptions_identifier', {
                    identifier : this.getIdentifier()
                }).then(response => {
                    this.isVisible = true
                    this.entry = response.data
                    this.loadSelectedPaymentMethod()
                    this.loadPersonalData()
                }).catch(error => {
                    this.error = error.response.data.message
                });
            }
        },
        loadSelectedPaymentMethod() {
            this.$axios.get('api/v1/payment_methods/available_payment_methods/?subscription=' + this.entry.uuid)
                .then(response => {
                    const paymentMethods = response.data.availablePaymentMethods
                    this.selectedPaymentMethod =  paymentMethods.find( pm => pm.id === this.entry.stripe_source );
                })
                .catch(error => {
                    this.selectedPaymentMethod = []
                });
        },
        loadPersonalData() {
            this.personalData = this.$root.checkoutReview.getData().personalData
        },
        getIdentifier() {
            return this.$route.params.identifier ? this.$route.params.identifier : null
        },
        closeCollapse() {
            this.isVisible = false
        },
        getDisplayLocation(address) {
            return this.$root.whitelabel.isUs() ?  `${address.house_number} ${address.street} - ${address.city} ${this.$t("regions." + address.region)} ${address.postal_code}` : `${address.postal_code} ${address.city}`
        },
        getTaxRateForWhitelabel() {
            let settings = this.$root.whitelabel.getSettings().global_config
            return Object.values(Object.values(settings)[0])[0].tax_rate
        },
    },
    computed: {
        ageVerificationNeeded(){
            return !this.$root.whitelabel.state.settings.noAgeVerification
        },
        paymentHandling() {
            if (this.entry?.subsidiary_has_own_payment_handling) {
                return false
            }
            return true
        },
        withoutTax() {
            return this.$root.whitelabel.setting('show_price_without_taxes')
        },
        isValid() {
            if (this.paymentHandling) {
                if (this.$root.whitelabel.setting('wlCountry') === 'us') {
                    return this.tocCheck && !this.isSubmitting && this.deliveryCheck && this.checkPersonalData && this.selectedPaymentMethod
                }
                return this.privacyCheck && this.tocCheck && !this.isSubmitting && this.deliveryCheck && this.checkPersonalData && this.selectedPaymentMethod
            }
            if (this.$root.whitelabel.setting('wlCountry') === 'us') {
                return this.tocCheck && !this.isSubmitting && this.deliveryCheck && this.checkPersonalData
            }
            return this.privacyCheck && this.tocCheck && !this.isSubmitting && this.deliveryCheck && this.checkPersonalData
        },
        getPrice() {
            if (this.entry) {
                const show_price_without_taxes = this.$root.whitelabel.setting('show_price_without_taxes')
                let apCosts = 0
                if (this.entry.additional_driver) {
                    apCosts += show_price_without_taxes
                        ? this.entry.additional_driver_article.price_net
                        : this.entry.additional_driver_article.price
                }
                if (this.entry.dp_reduction) {
                    apCosts += show_price_without_taxes
                        ? this.entry.dp_reduction_article.price_net
                        : this.entry.dp_reduction_article.price
                }

                return this.$root.whitelabel.getFormatetPrice((show_price_without_taxes ? this.entry.price_plan.price_without_tax : this.entry.price_plan.price) + apCosts)
            }
            return ''
        },
        getPriceWithoutAdditionalCosts() {
            if (this.entry) {
                const show_price_without_taxes = parseInt(this.$root.whitelabel.setting('show_price_without_taxes') ) === 1
                return this.$root.whitelabel.getFormatetPrice(show_price_without_taxes ? this.entry.price_plan.price_without_tax : this.entry.price_plan.price)
            }
            return ''
        },
        getSubsidiary() {
            if (this.product) {
                return this.product.subsidiary.name
            }
            return ''
        },
        getPriceWithCoupon() {
            const taxRate = this.getTaxRateForWhitelabel()
            const showPriceWithoutTaxes = this.$root.whitelabel.setting('show_price_without_taxes');
            const price = showPriceWithoutTaxes ? this.entry.price_plan.price_without_tax : this.entry.price_plan.price;
            const calculatedCouponValue = showPriceWithoutTaxes ? (this.couponValue / (taxRate * 100)) * 100 : this.couponValue;

            let newPrice = null;
            if (this.couponType === 'percent') {
                newPrice = (price / 100) * (100 - this.couponValue);
            } else {
                newPrice = price - calculatedCouponValue;
            }

            return `${this.$root.whitelabel.getFormatetPrice(newPrice)}`;
        },
        getCouponValue() {
            return this.couponValue;
        },
        getCouponValueWithoutTax() {
            const taxRate = this.getTaxRateForWhitelabel()
            return this.$root.whitelabel.getFormatetPrice((this.couponValue / (taxRate * 100)) * 100)
        },
        getDeliveryCosts() {
            if (this.entry) {
                const price = this.$root.whitelabel.setting('show_price_without_taxes') ? this.entry.delivery.deliveryCosts_net : this.entry.delivery.deliveryCosts
                return `${this.$root.whitelabel.getFormatetPrice(price)}`
            }

            return ''
        },
        willBePickedUp() {
            return this.entry && this.entry.delivery.deliveryMethod === 'pickup'
        },
        getCustomerType() {
            if (this.personalData) {
                return this.personalData.customerTypes.value === 1 ? this.$t('co.personal_data.customer_type.business') : this.$t('co.personal_data.customer_type.private')
            } else {
                return ''
            }
        },
        getCompany() {
            if (this.personalData) {
                return this.personalData.customerTypes.value
            } else {
                return false
            }
        },
        getFullName() {
            if (this.personalData) {
                return `${this.personalData.firstname.value} ${this.personalData.lastname.value}`
            }
        },
        checkPersonalData() {
            if (this.personalData) {
                this.personalDataError = 0
                if (this.getFullName.length <= 1) {
                    this.personalDataError++
                }

                if (this.ageVerificationNeeded && !this.personalData.birthday.value.length) {
                    this.personalDataError++
                }

                if (this.personalData.telephone.value == null) {
                    this.personalDataError++
                }

                if (this.personalDataError) {
                    return false
                } else {
                    return true
                }
            } else {
                return false
            }
        },
        getBirthday() {
            if (this.personalData) {
                return this.$d(new Date(this.personalData.birthday.value), 'short')
            } else {
                return ''
            }
        },
        getTelephone() {
            if (this.personalData) {
                return this.personalData.telephone.value
            } else {
                return ''
            }
        },
        deliveryEqualsInvoice() {
            if (this.entry) {
                return this.entry.delivery_address_uuid === this.entry.invoice_address_uuid
            } return false

        },
        getDeliveryAddress() {
            if (this.entry && this.entry.delivery_address_uuid !== null) {
                this.deliveryCheck = true
                const { street, house_number, postal_code, city, country, region} = this.entry.delivery_address_data
                const regionString = region ? `${this.$t('regions.'+region)}, ` : ''
                switch(this.$root.whitelabel.setting('wlCountry')) {
                    case 'us':
                        return `${house_number} ${street}<br> ${city} ${regionString} ${postal_code}<br> ${this.$t('countries.'+country)}`
                        break;
                    default:
                        return `${street} ${house_number}<br> ${postal_code} ${city}<br> ${regionString} ${this.$t('countries.'+country)}`
                }
            } else if (this.user && this.user.delivery_address) {
                this.deliveryCheck = true
                const { street, house_number, postal_code, city, country, region} = this.user.delivery_address
                const regionString = region ? `${this.$t('regions.'+region)}, ` : ''
                switch(this.$root.whitelabel.setting('wlCountry')) {
                    case 'us':
                        return `${house_number} ${street}<br>${city} ${regionString} ${postal_code}<br> ${this.$t('countries.'+country)}`
                        break;
                    default:
                        return `${street} ${house_number}<br> ${postal_code} ${city}<br> ${regionString} ${this.$t('countries.'+country)}`
                }
            } else {
                this.deliveryCheck = false
                return `${this.$t('co.address_data.delivery_address.reminder')}`
            }

        },
        getInvoiceAddress() {
            if (this.entry && this.entry.invoice_address_uuid !== null) {
                const { street, house_number, postal_code, city, country, region} = this.entry.invoice_address_data
                const regionString = region ? `${this.$t('regions.'+region)}, ` : ''
                switch(this.$root.whitelabel.setting('wlCountry')) {
                    case 'us':
                        return `${house_number} ${street}<br> ${city} ${regionString} ${postal_code}<br> ${this.$t('countries.'+country)}`
                        break;
                    default:
                        return `${street} ${house_number}<br> ${postal_code} ${city}<br> ${regionString} ${this.$t('countries.'+country)}`
                }
            } else {
                return ''
            }
        },
        getPaymentComponent() {
            if (this.selectedPaymentMethod) {
                switch(this.selectedPaymentMethod.type) {
                case "card":
                    return "stripe-card-info"
                case "sepa_debit":
                    return "stripe-sepa-info"
                default:
                    return "stripe-no-info"
                }
            } else {
                return "stripe-no-info"
            }
        },
        getDeliveryMethod() {
            if (this.entry) {
                return this.$t(`cdb.subscriptions.delivery.${this.entry.delivery.deliveryMethod}`)
            } else {
                return ''
            }
        },
        returnDisplayLocation() {
            return this.product ? this.getDisplayLocation(this.product.first_return_location) : ''
        },
        pickupDisplayLocation() {
            return this.product ? this.getDisplayLocation(this.product.address) : ''
        },
        hasPickupLocation() {
            return this.product ? this.product.hasOwnProperty('first_return_location') : false
        },
        sameAddress() {
            if (!this.product || !this.product.hasOwnProperty('first_return_location')) {
                return false
            } else {
                return this.pickupDisplayLocation === this.returnDisplayLocation
            }
        },
        isMarketplaceOwner() {
            if (this.product.subsidiary.uuid === this.$root.whitelabel.sub_id())
                return true
            else
                return false
        },
    },
    mounted() {
        EventBus.$on('close-all-collapses', this.closeCollapse)
        EventBus.$on('checkout-open-step-' + this.currentStep, this.init);
    }
}
</script>
